import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useBazi } from '../contexts/BaziContext';
import { hasBaziHistory, getBaziRecords } from '../utils/PrivacyUtils';
import InkParticles from '../components/InkParticles';
import ZhiChatModal from '../components/ZhiChatModal';
import { OnboardingModal, GuideFab } from '../components/OnboardingModal';

// ==================== 数据定义 ====================

const CORE_SERVICE_CATEGORIES = [
  {
    label: '看自己',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    items: [
      { icon: '💼', title: '职业建议', subtitle: '适合什么工作', path: '/career', featureId: 'career', badge: '会员', style: 'border-zhusha/20 hover:border-zhusha bg-zhusha/5', textColor: 'text-zhusha' },
      { icon: '📚', title: '学业建议', subtitle: '学习风格与方向', path: '/study', featureId: 'study', badge: '会员', style: 'border-blue-400/20 hover:border-blue-400 bg-blue-50', textColor: 'text-blue-600' },
      { icon: '🔮', title: '大运流年', subtitle: '十年运势趋势', path: '/fortune-cycles', featureId: 'fortune-cycles', badge: '会员', style: 'border-purple-500/20 hover:border-purple-500 bg-purple-50', textColor: 'text-purple-600' },
    ],
  },
  {
    label: '看关系',
    color: 'text-pink-600',
    bg: 'bg-pink-50',
    items: [
      { icon: '💕', title: '择偶匹配', subtitle: '八字合盘看缘分', path: '/love', featureId: 'love', badge: '会员', style: 'border-rose-400/20 hover:border-rose-400 bg-rose-50', textColor: 'text-rose-500' },
      { icon: '📅', title: '择日决策', subtitle: '出行签约选日子', path: '/select-date', featureId: 'select-date', badge: '会员', style: 'border-stone/20 hover:border-stone bg-stone/5', textColor: 'text-ink' },
    ],
  },
  {
    label: '看时机',
    color: 'text-green-600',
    bg: 'bg-green-50',
    items: [
      { icon: '🌅', title: '每日运势', subtitle: '今天怎么样', path: '/fortune', featureId: 'fortune', badge: '会员', style: 'border-gold/20 hover:border-gold bg-gold/5', textColor: 'text-gold' },
      { icon: '💰', title: '投资建议', subtitle: '风险偏好与时机', path: '/invest', featureId: 'invest', badge: '会员', style: 'border-emerald-400/20 hover:border-emerald-400 bg-emerald-50', textColor: 'text-emerald-600' },
      { icon: '🏠', title: '生活指南', subtitle: '健康与五行平衡', path: '/life-guide', featureId: 'life-guide', badge: '会员', style: 'border-teal-500/20 hover:border-teal-500 bg-teal-50', textColor: 'text-teal-600' },
      { icon: '📖', title: '能量日记', subtitle: '记录每日能量变化', path: '/energy-diary', featureId: 'energy-diary', badge: '会员', style: 'border-amber-400/20 hover:border-amber-400 bg-amber-50', textColor: 'text-amber-600' },
    ],
  },
];

const TRY_NOW = [
  { icon: '⚖️', title: '称骨算命', subtitle: '袁天罡称骨法 · 骨重断吉凶', path: '/bone-weight', highlight: true },
  { icon: '🎴', title: '每日一签', subtitle: '不需排盘，每天一张卦', path: '/daily-sign', highlight: true },
  { icon: '📖', title: '图解知识角', subtitle: '五行生克 · 四柱解析 · 常见问题', path: '/learn', highlight: false },
  { icon: '📋', title: '称骨记录', subtitle: '查看历史称骨结果', path: '/bone-weight-history', highlight: false },
];

const CLASSIC_QUOTES = [
  { text: '不知命，无以为君子也', source: '《论语·尧曰》' },
  { text: '天行健，君子以自强不息', source: '《周易·乾卦》' },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const cardItem = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 }
};

// ==================== 子组件 ====================

/** 区段标题 */
function SectionHeader({ title, subtitle }) {
  return (
    <div className="text-center mb-8">
      <h2 className="text-xl sm:text-2xl font-calligraphy text-ink mb-2">{title}</h2>
      {subtitle && <p className="text-xs sm:text-sm text-stone/50">{subtitle}</p>}
      <hr className="ink-divider mt-4 max-w-[200px] mx-auto" />
    </div>
  );
}

/** 知知先生横幅 — 首页顶部，主动引导 */
function ZhiBanner({ hasBazi, baziData }) {
  const navigate = useNavigate();
  const [zhiOpen, setZhiOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #FFF8EB 0%, #FFF3DC 40%, #FFE8C0 100%)',
          borderBottom: '1px solid rgba(212,175,55,0.2)',
        }}
      >
        {/* 装饰元素 */}
        <div className="absolute top-0 right-0 w-40 h-full opacity-[0.04] pointer-events-none">
          <span className="absolute -top-4 -right-4 text-[160px]">🦊</span>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent" />

        <div className="max-w-3xl mx-auto px-4 py-5 sm:py-6">
          <div className="flex items-center gap-4 sm:gap-5">
            {/* 头像 */}
            <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-400 to-amber-500 flex items-center justify-center text-3xl shadow-lg shadow-amber-200/50 ring-2 ring-amber-200/30">
              🦊
            </div>

            {/* 文案区 */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-base sm:text-lg font-calligraphy text-ink font-bold">
                  知知先生 · AI命理顾问
                </h2>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200/60 text-amber-700 font-medium">AI</span>
              </div>
              <p className="text-sm text-stone/60 mb-0.5">
                知天命，知人事
              </p>

            </div>

            {/* 按钮区 — 仅已排盘时显示操作按钮，未排盘时不抢占CTA */}
            {hasBazi ? (
              <div className="shrink-0 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setZhiOpen(true)}
                  className="px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-xl text-sm font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95 whitespace-nowrap"
                >
                  开始咨询
                </button>
                <button
                  onClick={() => navigate('/result')}
                  className="px-4 py-2.5 border border-amber-300/60 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-100/50 transition-all whitespace-nowrap"
                >
                  我的命盘
                </button>
              </div>
            ) : (
              <span className="shrink-0 text-xs text-stone/40 hidden sm:block">
                排盘后可咨询 →
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {zhiOpen && (
        <ZhiChatModal
          isOpen={zhiOpen}
          onClose={() => setZhiOpen(false)}
          baziData={baziData}
        />
      )}
    </>
  );
}

/** 知知先生区域 — 首页中段引导区（保留原位置，文案升级） */
function ZhiSection({ hasBazi, baziData }) {
  const navigate = useNavigate();
  const [zhiOpen, setZhiOpen] = useState(false);

  const quickQuestions = [
    '事业方向', '财运分析', '感情婚姻',
    '流年运势', '五行喜忌', '开运建议',
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden rounded-2xl p-6 sm:p-8"
        style={{
          background: 'linear-gradient(135deg, rgba(255,248,235,0.9) 0%, rgba(255,243,220,0.85) 100%)',
          border: '1px solid rgba(212,175,55,0.15)',
        }}
      >
        {/* 装饰 */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
          <span className="text-8xl">🦊</span>
        </div>

        <div className="flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center text-2xl shadow-lg">
            🦊
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-calligraphy text-ink mb-1">
              知知先生已准备好为您解读
            </h3>
            <p className="text-sm text-stone/60 mb-4 leading-relaxed">
              {hasBazi
                ? '您的八字命盘已排好，知知先生已为您备好解读，随时可以开始'
                : '排盘后即可让知知先生为您深度解读命盘'}
            </p>

            {/* 快捷问题标签 */}
            <div className="flex flex-wrap gap-2 mb-4">
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    if (hasBazi) {
                      setZhiOpen(true);
                    } else {
                      navigate('/input');
                    }
                  }}
                  className="px-3 py-1.5 bg-white/80 border border-amber-200/50 rounded-full text-xs text-stone/70 hover:text-amber-700 hover:border-amber-300 hover:bg-white transition-all"
                >
                  {q}
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                if (hasBazi) {
                  setZhiOpen(true);
                } else {
                  navigate('/input');
                }
              }}
              className="px-5 py-2 rounded-full text-sm font-medium transition-all bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-lg hover:shadow-xl hover:scale-105"
            >
              {hasBazi ? '开始咨询 🦊' : '开始排盘 →'}
            </button>
          </div>
        </div>
      </motion.div>

      {zhiOpen && (
        <ZhiChatModal
          isOpen={zhiOpen}
          onClose={() => setZhiOpen(false)}
          baziData={baziData}
        />
      )}
    </>
  );
}

/** 命盘摘要卡片（已排盘时显示） */
function BaziSummaryCard({ baziData }) {
  const { getPillarSummary, getUsefulGod } = useBazi();
  const navigate = useNavigate();

  const pillarSummary = getPillarSummary();
  const usefulGod = getUsefulGod();
  const pattern = baziData?.pattern || '—';
  const dayMaster = baziData?.day_master || '—';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl p-5 shadow-lg border border-amber-200/60"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-ink flex items-center gap-2">
          <span className="text-amber-500">☯</span> 当前命盘
        </h3>
        <button
          onClick={() => navigate('/result')}
          className="text-xs text-zhusha hover:text-red-800 transition-colors underline underline-offset-2"
        >
          查看详情
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        <span className="font-serif text-base text-ink font-semibold">
          {pillarSummary}
        </span>
        <span className="text-stone/30 hidden sm:inline">|</span>
        <span className="text-stone/70">
          日主：<span className="font-medium text-ink">{dayMaster}</span>
        </span>
        <span className="text-stone/30 hidden sm:inline">|</span>
        <span className="text-zhusha font-medium">
          {pattern}
        </span>
        <span className="text-stone/30 hidden sm:inline">|</span>
        <span className="text-stone/70">
          用神：<span className="text-amber-600 font-medium">{usefulGod}</span>
        </span>
      </div>
    </motion.div>
  );
}

/** 模块卡片 — ToolsPanel 风格：带独立配色+Badge+引导符 */
function ModuleCard({ service, hasBazi, isMember, isFree, singleUnlocked, navigate }) {
  const featureId = service.featureId;

  // 已排盘 + 会员 → 直接进入
  const isUnlocked = hasBazi && isMember;
  // 已排盘 + 单次解锁此板块
  const isSingleUnlocked = hasBazi && singleUnlocked && singleUnlocked.includes(featureId);
  // 已排盘 + 免费用户 → 需要付费
  const needsPaywall = hasBazi && isFree && !isSingleUnlocked;
  // 未排盘 → 灰显
  const isLocked = !hasBazi;

  const handleClick = () => {
    if (isLocked) {
      navigate(`/input?returnTo=${encodeURIComponent(service.path)}`);
    } else if (needsPaywall) {
      navigate('/membership');
    } else {
      navigate(service.path);
    }
  };

  return (
    <motion.button
      variants={cardItem}
      onClick={handleClick}
      className={`p-4 rounded-2xl border ${service.style} transition-all text-left group relative overflow-hidden ${
        isLocked ? 'opacity-60' : 'cursor-pointer'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{service.icon}</span>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full font-medium border"
          style={{
            backgroundColor: service.badge === '免费' ? '#f0fdf4' : '#fef3c7',
            color: service.badge === '免费' ? '#16a34a' : '#d97706',
            borderColor: service.badge === '免费' ? '#bbf7d0' : '#fde68a',
          }}
        >
          {service.badge}
        </span>
        {isSingleUnlocked && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
            已解锁
          </span>
        )}
      </div>
      <div className={`text-sm font-bold ${service.textColor} mb-1`}>{service.title}</div>
      <p className="text-[11px] leading-relaxed text-stone/50">{service.subtitle}</p>
      {needsPaywall ? (
        <div className="mt-2">
          <span className="inline-block text-[10px] px-2 py-0.5 rounded-lg bg-green-50 text-green-600 font-medium border border-green-200">
            需会员
          </span>
        </div>
      ) : (
        <div className="mt-2 flex justify-end">
          <span className={`text-xs font-medium ${service.textColor} opacity-50 group-hover:opacity-100 transition-opacity`}>
            进入 →
          </span>
        </div>
      )}
    </motion.button>
  );
}

// ==================== 主页面 ====================

export default function HomePage() {
  const navigate = useNavigate();
  const { isMember, isFree, singleUnlocked } = useAuth();
  const { isBaziLoaded, baziData, getPillarSummary, getUsefulGod } = useBazi();
  const hasBazi = isBaziLoaded;


  return (
    <div className="relative min-h-screen bg-rice-paper">
      {/* 墨粒子背景 */}
      <InkParticles />

      {/* 首次访问引导弹窗 + 右下角悬浮指南 */}
      <OnboardingModal />
      <GuideFab />

      {/* ===== 知知先生横幅 — 全宽顶部 ===== */}
      <div className="relative z-20">
        <ZhiBanner hasBazi={hasBazi} baziData={baziData} />
      </div>

      {/* 页面内容 */}
      <div className="relative z-10">
        <div className="max-w-3xl mx-auto px-4 pb-16">

          {/* ===== 首屏 ===== */}
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center pt-12 sm:pt-16 mb-10"
          >
            {/* Logo */}
            <div className="w-18 h-18 sm:w-22 sm:h-22 mx-auto mb-5 sm:mb-6 rounded-full bg-gradient-to-br from-zhusha to-red-800 flex items-center justify-center shadow-xl"
              style={{ width: '72px', height: '72px' }}
            >
              <span className="text-3xl sm:text-4xl text-white font-calligraphy">命</span>
            </div>

            {/* 标题 */}
            <h1 className="text-4xl sm:text-5xl font-calligraphy text-ink mb-3 tracking-wider">
              命盘职引
            </h1>
            <p className="text-stone/60 text-sm sm:text-base mb-1 tracking-wide">
              你的八字决策伙伴
            </p>
            <p className="text-stone/40 text-xs sm:text-sm mb-8 tracking-wide">
              排盘是确定的，解读是个性化的
            </p>

            {/* ===== 已排盘：显示命盘摘要卡片 ===== */}
            {hasBazi ? (
              <BaziSummaryCard baziData={baziData} />
            ) : (
              <>
                {/* ===== 未排盘：大按钮 ===== */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/input')}
                  className="btn-primary w-60 max-w-full py-3.5 sm:py-4 text-lg sm:text-xl font-medium rounded-full"
                >
                  开始排盘
                </motion.button>

                {/* 次要入口 */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4 flex items-center justify-center gap-3 text-sm text-stone/40"
                >
                  <span>或先体验：</span>
                  <Link
                    to="/bone-weight"
                    className="text-stone/50 hover:text-stone/80 transition-colors underline underline-offset-4 decoration-stone/20 hover:decoration-stone/40"
                  >
                    称骨
                  </Link>
                  <span>·</span>
                  <Link
                    to="/daily-sign"
                    className="text-stone/50 hover:text-stone/80 transition-colors underline underline-offset-4 decoration-stone/20 hover:decoration-stone/40"
                  >
                    每日一签
                  </Link>
                </motion.div>
              </>
            )}
          </motion.div>

          {/* ===== 核心服务（分类展示） ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <SectionHeader title="核心服务" />

            {/* 未排盘统一提示 */}
            {!hasBazi && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center text-xs text-stone/40 mb-6"
              >
                🔒 以下服务需要排盘后才能使用，点击任意工具即可开始
              </motion.p>
            )}

            <div className="space-y-6">
              {CORE_SERVICE_CATEGORIES.map((cat) => (
                <div key={cat.label}>
                  {/* 分类标签 */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${cat.bg} ${cat.color}`}>
                      {cat.label}
                    </span>
                    <div className="flex-1 h-px bg-stone/8" />
                  </div>

                  <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-20px' }}
                    className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                  >
                    {cat.items.map((svc) => (
                      <ModuleCard
                        key={svc.title}
                        service={svc}
                        hasBazi={hasBazi}
                        isMember={isMember}
                        isFree={isFree}
                        singleUnlocked={singleUnlocked}
                        navigate={navigate}
                      />
                    ))}
                  </motion.div>
                </div>
              ))}
            </div>

            {/* 底部 — 查看全部工具 */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-6"
            >
              <Link
                to="/tools"
                className="inline-flex items-center gap-1 text-xs text-stone/40 hover:text-stone/70 transition-colors"
              >
                查看全部工具 <span>→</span>
              </Link>
            </motion.div>
          </motion.section>

          {/* ===== 即刻体验（无需排盘） ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <SectionHeader
              title="即刻体验"
              subtitle="无需排盘，马上探索"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TRY_NOW.map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                >
                  <Link
                    to={item.path}
                    className={`glass-card p-5 rounded-2xl flex items-center gap-4 transition-all duration-300 group ${
                      item.highlight ? 'ring-1 ring-amber-300/50' : ''
                    }`}
                  >
                    <span className="text-3xl shrink-0">{item.icon}</span>
                    <div className="min-w-0">
                      <h3 className={`text-base font-bold mb-1 ${item.highlight ? 'text-amber-700' : 'text-ink'}`}>
                        {item.title}
                        {item.highlight && (
                          <span className="ml-2 text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full align-middle">
                            推荐
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-stone/50">{item.subtitle}</p>
                    </div>
                    <span className="shrink-0 text-stone/20 group-hover:text-stone/40 transition-colors text-lg">
                      →
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ===== 知知先生区域 ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <ZhiSection hasBazi={hasBazi} baziData={baziData} />
          </motion.section>

          {/* ===== Footer ===== */}
          <motion.footer
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="border-t border-stone/10 pt-8 space-y-6"
          >
            {/* 导航链接 */}
            <div className="flex flex-wrap justify-center gap-4 text-xs text-stone/40">
              <Link to="/about" className="hover:text-stone/70 transition-colors">关于我们</Link>
              <Link to="/privacy" className="hover:text-stone/70 transition-colors">隐私政策</Link>
              <Link to="/terms" className="hover:text-stone/70 transition-colors">服务条款</Link>
              <Link to="/contact" className="hover:text-stone/70 transition-colors">联系我们</Link>
            </div>

            {/* 简化隐私说明 */}
            <div className="text-xs leading-relaxed text-stone/40 max-w-lg mx-auto space-y-2.5">
              <p className="font-medium text-stone/50 text-center mb-3">你的数据怎么用？</p>
              <div className="space-y-1.5">
                <p>✅ 排盘计算：本地完成，不上传服务器</p>
                <p>✅ 问知知先生：只传命盘数据，不传身份信息</p>
                <p>✅ 历史记录：存在你的设备，我们看不到</p>
              </div>
            </div>

            {/* 版权 */}
            <div className="text-center pt-4 space-y-1">
              <p className="text-[10px] text-stone/25">
                © {new Date().getFullYear()} 命盘职引 MingPan ZhiYin. All rights reserved.
              </p>
              <p className="text-[10px] text-stone/20">
                八字命理学源于中国传统智慧，请理性参考，切勿迷信
              </p>
            </div>
          </motion.footer>

        </div>
      </div>
    </div>
  );
}
